<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ru';
import BasketRow from './BasketRow.vue';
import { useCheckoutFormStore } from '@features/basket';
import { useInstitutionStore } from '@entities/institution';
import { useModalStore, modalStateEnum } from '@shared/model';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ru');

// Фолбэк, если бэк ещё не отдаёт workload-поля (старая отдача / новый филиал без миграции).
const FALLBACK_PREP_MIN = 15;
const FALLBACK_PREP_MAX = 25;

const checkoutFormStore = useCheckoutFormStore();
const storeInstitution = useInstitutionStore();
const storeModal = useModalStore();

const branchTimezone = computed(
  () => storeInstitution.selectedBranch?.working_hours?.timezone || 'Europe/Moscow',
);

const form = computed(() => checkoutFormStore.getForm);

const isScheduled = computed(
  () => form.value.preorder_mode === 'scheduled' && !!form.value.reservation_time,
);

const prepRange = computed<{ min: number; max: number }>(() => {
  const branch = storeInstitution.selectedBranch as any;

  // Время готовки — per-branch из настроек предзаказа, фолбэк на effective_kitchen (workload).
  const prepMin =
    branch?.preorder_prep_time_min ?? branch?.effective_kitchen_time_min ?? FALLBACK_PREP_MIN;
  const prepMax =
    branch?.preorder_prep_time_max ?? branch?.effective_kitchen_time_max ?? FALLBACK_PREP_MAX;

  // Время доставки — зональное (workload-control). Не дублируем в preorder-настройках.
  if (form.value.method_receipt === 'delivery') {
    const courierMin = branch?.effective_courier_time_min ?? 0;
    const courierMax = branch?.effective_courier_time_max ?? 0;
    return { min: prepMin + courierMin, max: prepMax + courierMax };
  }
  return { min: prepMin, max: prepMax };
});

// Скрываем кнопку выбора времени, если предзаказ выключен в админке
const isPreorderEnabled = computed(() => storeInstitution.selectedBranch?.preorder_enabled !== false);

const title = computed(() => {
  if (!isScheduled.value) {
    const { min, max } = prepRange.value;
    return min === max ? `В течение ${min} мин` : `В течение ${min}–${max} мин`;
  }
  const local = dayjs(form.value.reservation_time as string).tz(branchTimezone.value);
  const today = dayjs().tz(branchTimezone.value).startOf('day');
  const target = local.startOf('day');
  const diffDays = target.diff(today, 'day');
  const time = local.format('HH:mm');
  if (diffDays === 0) return `Сегодня к ${time}`;
  if (diffDays === 1) return `Завтра к ${time}`;
  return `${local.format('D MMM')} к ${time}`;
});

const subtitle = computed(() =>
  form.value.method_receipt === 'delivery'
    ? 'Изменить время доставки'
    : 'Изменить время получения',
);

function openModal() {
  storeModal.open(modalStateEnum.PreorderTime);
}
</script>

<template>
  <BasketRow :clickable="isPreorderEnabled" @click="isPreorderEnabled && openModal()">
    <template #icon>
      <UIcon name="i-heroicons-clock" class="w-5 h-5" />
    </template>
    <span class="basket-row__title">{{ title }}</span>
    <span v-if="isPreorderEnabled" class="basket-row__subtitle">{{ subtitle }}</span>
  </BasketRow>
</template>
